import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';

const historyLocation = 'recommendation_history';

function navigate({
  routeLocation,
  additionalParams,
  navigateToRoute,
  scope,
}, op = {}) {
  const {
    accountId,
    adGroupId,
    campaignId,
    customerId,
  } = scope;
  let levelAt = op.scope || scope.levelAt;

  // history page only support account level
  if (routeLocation === historyLocation) {
    levelAt = levelAtConstant.ACCOUNT;
  }

  let params = _.extend({
    account_id: accountId,
    adgroup_id: adGroupId,
    campaign_id: campaignId,
    customer_id: customerId,
  }, additionalParams);
  let route = '';

  switch (levelAt) {
    case levelAtConstant.CAMPAIGN:
      route = `campaign_${routeLocation}`;
      params = _.omit(params, 'adgroup_id');
      break;
    case levelAtConstant.ADGROUP:
      route = `adgroup_${routeLocation}`;
      break;
    case levelAtConstant.ACCOUNT:
    default:
      route = routeLocation;
      params = _.omit(params, 'campaign_id', 'adgroup_id');
      break;
  }

  navigateToRoute(route, params);
}

function viewCategory(navigateToRoute, scope, page, category) {
  return () => navigate({
    additionalParams: { category },
    navigateToRoute,
    routeLocation: page,
    scope,
  });
}

function backToRecommendation(
  navigateToRoute,
  scope,
  page,
  category,
  type,
  campaignId,
  adGroupId
) {
  let routeLocation = page;
  let additionalParams = { category };
  if (type) {
    routeLocation = `${page}_details`;
    additionalParams = { type };
  }

  const tempScope = _.extend({}, scope);
  if (adGroupId) {
    tempScope.levelAt = levelAtConstant.ADGROUP;
    _.extend(additionalParams, {
      campaign_id: campaignId,
      adgroup_id: adGroupId,
    });
  } else if (campaignId) {
    tempScope.levelAt = levelAtConstant.CAMPAIGN;
    _.extend(additionalParams, {
      campaign_id: campaignId,
    });
  }

  return () => navigate({
    additionalParams,
    navigateToRoute,
    routeLocation,
    scope: tempScope,
  });
}

function viewHistory(navigateToRoute, scope, validDetailPage) {
  validDetailPage();
  return navigate({
    navigateToRoute,
    routeLocation: historyLocation,
    scope,
  });
}

function viewSummary(navigateToRoute, scope, page, op) {
  return navigate({
    navigateToRoute,
    routeLocation: page,
    scope,
  }, op);
}

function viewDetails(navigateToRoute, scope, page, validDetailPage, type, data = {}) {
  const newScope = { ...scope, ...data };
  validDetailPage();
  return () => navigate({
    additionalParams: { type },
    navigateToRoute,
    routeLocation: `${page}_details`,
    scope: newScope,
  });
}

export const withNavigation = (WrappedComponent) => {
  const HoC = (props) => {
    const {
      scope,
      page,
      deps: {
        legacyRouter,
      },
      validDetailPage,
      /**
       * Only for test
       */
      testRef,
    } = props;

    const viewCategoryHandler = _.partial(viewCategory, legacyRouter.navigateToRoute, scope, page);
    const viewSummaryHandler = _.partial(viewSummary, legacyRouter.navigateToRoute, scope, page);
    const viewDetailsHandler = _.partial(
      viewDetails,
      legacyRouter.navigateToRoute,
      scope,
      page,
      validDetailPage
    );
    const viewHistoryHandler = _.partial(viewHistory, legacyRouter.navigateToRoute, scope, validDetailPage);

    if (testRef) {
      testRef.viewCategory = viewCategoryHandler;
      testRef.viewSummary = viewSummaryHandler;
      testRef.viewDetails = viewDetailsHandler;
      testRef.viewHistory = viewHistoryHandler;
    }

    return (
      <WrappedComponent
        {...props}
        viewCategory={viewCategoryHandler}
        backToRecommendation={_.partial(
          backToRecommendation,
          legacyRouter.navigateToRoute,
          scope,
          page
        )}
        viewSummary={viewSummaryHandler}
        viewDetails={viewDetailsHandler}
        viewHistory={viewHistoryHandler}
      />
    );
  };

  HoC.displayName = `withNavigation(${getDisplayName(WrappedComponent)})`;
  HoC.propTypes = {
    page: PropTypes.string,
    scope: PropTypes.shape({
      accountId: PropTypes.number,
      adGroupId: PropTypes.number,
      campaignId: PropTypes.number,
      customerId: PropTypes.number,
      levelAt: PropTypes.string,
    }),
    deps: PropTypes.shape({
      legacyRouter: PropTypes.object.isRequired,
    }).isRequired,
    validDetailPage: PropTypes.func.isRequired,
  };
  HoC.defaultProps = {
    page: 'recommendation',
    scope: {},
  };

  hoistNonReactStatics(HoC, WrappedComponent);

  return HoC;
};
